import { countsFor, searchPeople } from '@tive/core';
import { CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn, departmentColor } from '../lib/utils';
import { resolveTheme } from '../lib/theme';
import { Avatar } from './Avatar';
import { useAppStore } from '../store/useAppStore';
import { Kbd } from './ui';

export function SearchPalette() {
  const open = useAppStore((s) => s.paletteOpen);
  const model = useAppStore((s) => s.model);
  const dark = useAppStore((s) => resolveTheme(s.theme) === 'dark');
  const setPalette = useAppStore((s) => s.setPalette);
  const revealPerson = useAppStore((s) => s.revealPerson);
  const openPanel = useAppStore((s) => s.openPanel);

  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const hits = useMemo(() => {
    if (!model) return [];
    if (query.trim().length === 0) {
      // Sensible default: the biggest teams, so the palette is useful when empty.
      return [...model.people]
        .filter((p) => (model.counts.get(p.id)?.directReports ?? 0) > 0)
        .sort(
          (a, b) => (model.counts.get(b.id)?.totalReports ?? 0) - (model.counts.get(a.id)?.totalReports ?? 0),
        )
        .slice(0, 8)
        .map((person) => ({ person, score: 0 }));
    }
    return searchPeople(model, query, 20);
  }, [model, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => setCursor(0), [query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPalette(!useAppStore.getState().paletteOpen);
      }
      if (event.key === 'Escape' && useAppStore.getState().paletteOpen) setPalette(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setPalette]);

  if (!open || !model) return null;

  const choose = (personId: string) => {
    setPalette(false);
    revealPerson(personId);
    openPanel('details');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Close search"
        onClick={() => setPalette(false)}
        className="absolute inset-0 cursor-default bg-black/40 backdrop-blur-[2px]"
      />
      <div className="card-in relative w-full max-w-xl overflow-hidden rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] shadow-[var(--shadow-panel)]">
        <div className="flex items-center gap-2 border-b border-[var(--color-line)] px-3">
          <Search size={15} className="text-[var(--color-ink-3)]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setCursor((index) => Math.min(index + 1, hits.length - 1));
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setCursor((index) => Math.max(index - 1, 0));
              }
              if (event.key === 'Enter') {
                const hit = hits[cursor];
                if (hit) choose(hit.person.id);
              }
            }}
            placeholder="Search by name, title, department or email…"
            className="h-12 flex-1 bg-transparent text-[14px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-3)] focus:outline-none"
          />
          <Kbd>Esc</Kbd>
        </div>

        <ul className="max-h-[52vh] overflow-y-auto p-1.5">
          {hits.length === 0 ? (
            <li className="px-3 py-6 text-center text-[12.5px] text-[var(--color-ink-3)]">
              Nobody matches “{query}”.
            </li>
          ) : null}

          {hits.map((hit, index) => {
            const counts = countsFor(model, hit.person.id);
            const dept = departmentColor(hit.person.department, dark);
            return (
              <li key={hit.person.id}>
                <button
                  type="button"
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => choose(hit.person.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
                    index === cursor ? 'bg-[var(--color-surface-2)]' : 'hover:bg-[var(--color-surface-2)]',
                  )}
                >
                  <Avatar person={hit.person} size={28} colors={dept} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-medium text-[var(--color-ink)]">
                      {hit.person.name}
                    </span>
                    <span className="block truncate text-[11px] text-[var(--color-ink-3)]">
                      {hit.person.title}
                      {hit.person.department ? ` · ${hit.person.department}` : ''}
                    </span>
                  </span>
                  {counts.directReports > 0 ? (
                    <span className="shrink-0 text-[10.5px] text-[var(--color-ink-3)] tabular-nums">
                      {counts.directReports} / {counts.totalReports}
                    </span>
                  ) : null}
                  {index === cursor ? (
                    <CornerDownLeft size={12} className="shrink-0 text-[var(--color-ink-3)]" />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
